import { captureException } from '@sentry/react'
import { useState } from 'react'

import type { ReactElement } from 'react'
import type { ISwitchButton } from '~/components/Buttons/SwitchButton'
import type { Filter } from '~/providers/Clusters'
import type { TFilterPreset } from '~/types/cluster'
import type { TExtendedDeviceType } from '~/types/device-type'

import SwitchButton from '~/components/Buttons/SwitchButton'
import CreatableSelectInput from '~/components/Inputs/Basic/CreatableSelectInput'
import MultiSelectInput from '~/components/Inputs/Basic/MultiSelectInput'
import SelectInput from '~/components/Inputs/Basic/SelectInput'
import { DEVICE_TYPE } from '~/const'
import { useClustersContext } from '~/providers/Clusters'
import { useSearchParamsContext } from '~/providers/SearchParams'

import './styles.scss'

const filterPresetOptions: { label: string; value: TFilterPreset }[] = [
  {
    label: 'All Apps',
    value: 'all',
  },
  {
    label: 'PM Check',
    value: 'pm_check',
  },
  {
    label: 'PM Analyze',
    value: 'pm_analyze',
  },
  {
    label: 'Publishing Targets',
    value: 'bd_contact_publishing',
  },
  {
    label: 'Contact Platform Registration',
    value: 'bd_contact_data',
  },
  {
    label: 'Leaders Analyze',
    value: 'leaders_analyze',
  },
]

interface Props {
  clusterTags?: string[]
}

const ClusterFilters = ({ clusterTags }: Props): ReactElement => {
  /** Local state */

  const { dispatchToSearchParams } = useSearchParamsContext()

  const { dispatch: dispatchToClustersContext, state } = useClustersContext()

  const { deviceType, filterPreset, listingMode, searchElements, tags } = state

  const [switchButtons, setSwitchButtons] = useState<ISwitchButton[]>([
    {
      content: 'All',
      id: 'all',
      isActive: deviceType === 'all',
    },
    {
      content: 'Android',
      id: DEVICE_TYPE.ANDROID,
      isActive: deviceType === 'android',
    },
    {
      content: 'iOS',
      id: DEVICE_TYPE.IOS,
      isActive: deviceType === 'ios',
    },
  ])

  /** Handlers */

  const handleFilterChanged = (filter: Filter) => {
    return (filterValue: unknown) => {
      switch (filter) {
        case 'deviceType':
          setSwitchButtons(
            switchButtons.map((button) => ({
              ...button,
              isActive: button.id === filterValue,
            }))
          )
          dispatchToClustersContext({ type: filter, value: filterValue })
          dispatchToSearchParams({ type: 'update', value: { deviceType: filterValue as TExtendedDeviceType } })
          break
        case 'filterPreset':
          const castedFilterPresetValue = filterValue as IOption | null

          dispatchToClustersContext({ type: filter, value: castedFilterPresetValue?.value })
          dispatchToSearchParams({
            type: 'update',
            value: { filterPreset: castedFilterPresetValue?.value as TFilterPreset },
          })
          break
        case 'searchElements':
          const castedSearchElementValue = filterValue as IOption[]

          const formattedSearchElements = castedSearchElementValue.map(({ value }) => value)

          dispatchToClustersContext({ type: filter, value: formattedSearchElements })
          dispatchToSearchParams({ type: 'update', value: { 'searchElements[]': formattedSearchElements } })
          break
        case 'tags':
          const castedFilterValue = filterValue as IOption[]

          const formattedTags = castedFilterValue.map(({ value }) => value)

          dispatchToClustersContext({ type: filter, value: formattedTags })
          dispatchToSearchParams({ type: 'update', value: { 'tags[]': formattedTags } })
          break
        default:
          captureException(new Error(`Unknown filter type: ${filter}`))
      }
    }
  }

  return (
    <div className="cluster-details-view__cluster-filters">
      <SwitchButton buttons={switchButtons} onButtonSelectionChange={handleFilterChanged('deviceType')} size="small" />
      {listingMode === 'cluster' ? (
        <>
          <SelectInput
            className="cluster-details-view__cluster-filters__filter-preset-input"
            onChange={handleFilterChanged('filterPreset')}
            options={filterPresetOptions}
            placeholder="Filters"
            size="small"
            value={filterPresetOptions.find((value) => value.value == filterPreset)}
          />
          {clusterTags ? (
            <MultiSelectInput
              className="cluster-details-view__cluster-filters__tags-input"
              name="tags"
              onChange={handleFilterChanged('tags')}
              options={clusterTags
                .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
                .map((el) => ({ label: el, value: el }))}
              placeholder="Filter by tags"
              size="small"
              value={tags.map((tag) => ({ label: tag, value: tag }))}
            />
          ) : null}
        </>
      ) : null}
      {listingMode === 'candidates' ? (
        <CreatableSelectInput
          className="cluster-details-view__cluster-filters__keywords-search-input"
          isMulti
          name="searchElements"
          onChange={handleFilterChanged('searchElements')}
          openMenuOnClick={false}
          placeholder='Type something and press "Enter"'
          size="small"
          value={searchElements.map((el) => ({ label: el, value: el }))}
        />
      ) : null}
    </div>
  )
}

export default ClusterFilters
